/**
 * Чистая логика журнала оценок (индекс ученик×дата и строки XLSX-экспорта).
 * Вынесено из JournalPage, чтобы покрываться unit-тестами.
 */
import type { Grade, Student } from '../types/entities'
import { weightedAverage } from './gradeMath.ts'

/** studentId → (date → оценки) для выбранного класса и (опционально) предмета. */
export function indexGradesByStudentAndDate(
  all: Grade[],
  classId: string,
  subjectId: string,
): Map<string, Map<string, Grade[]>> {
  const map = new Map<string, Map<string, Grade[]>>()
  for (const g of all) {
    if (g.classId !== classId) continue
    if (subjectId && g.subjectId !== subjectId) continue
    const studentMap = map.get(g.studentId) ?? new Map<string, Grade[]>()
    const arr = studentMap.get(g.date) ?? []
    arr.push(g)
    studentMap.set(g.date, arr)
    map.set(g.studentId, studentMap)
  }
  return map
}

/**
 * Таблица для XLSX-экспорта: заголовок «Ученик | дд.мм … | Средний» и строка
 * на ученика. Несколько оценок за день — через «/»; в среднее идут только
 * числовые значения (зачёт/«н»/буквы пропускаются).
 */
export function buildJournalExportRows(
  students: Student[],
  dates: string[],
  gradesByCell: Map<string, Map<string, Grade[]>>,
  labels: { student: string; average: string },
): string[][] {
  const header = [labels.student, ...dates.map((d) => `${d.slice(8, 10)}.${d.slice(5, 7)}`), labels.average]
  const rows = students.map((s) => {
    const cell = gradesByCell.get(s.id)
    const all: Grade[] = []
    const dateCells = dates.map((d) => {
      const gs = cell?.get(d) ?? []
      all.push(...gs)
      return gs.map((g) => g.value).join('/')
    })
    // тот же средневзвешенный балл, что и в таблице журнала
    const avgN = weightedAverage(all)
    const avg = avgN == null ? '' : avgN.toFixed(2)
    return [`${s.lastName} ${s.firstName}`, ...dateCells, avg]
  })
  return [header, ...rows]
}
